import React, { createContext, useContext, useState, useEffect } from "react";

type Language = "sv" | "en" | "de";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  sv: {
    nav_home: "Hem",
    nav_catalog: "Katalog",
    nav_webshop: "Shop",
    nav_blog: "Blogg",
    nav_about: "Om oss",
    nav_contact: "Kontakt",
    nav_experiences: "Upplevelser",
    nav_usecases: "Användningsområden",
    hero_title: "Diamond Domes",
    hero_subtitle: "Gör världen till en rundare plats",
    hero_cta1: "Se vårat utbud",
    hero_cta2: "Till Webbshopen",
    mission_title: "Vårt Uppdrag",
    mission_description: "Vi skapar unika geodetiska kupoldome som kombinerar hållbarhet med estetik. Våra strukturer är byggda för att vara i harmoni med naturen och erbjuda ett enastående boende.",
    benefits_title: "Fördelar",
    benefit_quality_title: "Premium Kvalitet",
    benefit_quality_description: "Handgjorda med precision och omsorg för att säkerställa hållbarhet och skönhet.",
    benefit_sustainability_title: "Hållbarhet",
    benefit_sustainability_description: "Miljövänliga material och energieffektiv design för en bättre framtid.",
    benefit_service_title: "Personlig Service",
    benefit_service_description: "Dedikerad support från idé till färdig konstruktion.",
  },
  en: {
    nav_home: "Home",
    nav_catalog: "Catalog",
    nav_webshop: "Shop",
    nav_blog: "Blog",
    nav_about: "About Us",
    nav_contact: "Contact",
    nav_experiences: "Experiences",
    nav_usecases: "Use cases",
    hero_title: "Diamond Domes",
    hero_subtitle: "Making the world a rounder place",
    hero_cta1: "View Our Range",
    hero_cta2: "To Webshop",
    mission_title: "Our Mission",
    mission_description: "We create unique geodesic dome structures that combine sustainability with aesthetics. Our structures are built to be in harmony with nature and offer an outstanding living experience.",
    benefits_title: "Benefits",
    benefit_quality_title: "Premium Quality",
    benefit_quality_description: "Handcrafted with precision and care to ensure durability and beauty.",
    benefit_sustainability_title: "Sustainability",
    benefit_sustainability_description: "Eco-friendly materials and energy-efficient design for a better future.",
    benefit_service_title: "Personal Service",
    benefit_service_description: "Dedicated support from concept to finished construction.",
  },
  de: {
    nav_home: "Startseite",
    nav_catalog: "Katalog",
    nav_webshop: "Shop",
    nav_blog: "Blog",
    nav_about: "Über uns",
    nav_contact: "Kontakt",
    nav_experiences: "Erlebnisse",
    nav_usecases: "Anwendungsbereiche",
    hero_title: "Diamond Domes",
    hero_subtitle: "Die Welt zu einem runderen Ort machen",
    hero_cta1: "Unser Sortiment ansehen",
    hero_cta2: "Zum Webshop",
    mission_title: "Unsere Mission",
    mission_description: "Wir schaffen einzigartige geodätische Kuppelstrukturen, die Nachhaltigkeit mit Ästhetik verbinden. Unsere Strukturen sind gebaut, um in Harmonie mit der Natur zu sein und ein außergewöhnliches Wohnerlebnis zu bieten.",
    benefits_title: "Vorteile",
    benefit_quality_title: "Premium-Qualität",
    benefit_quality_description: "Handgefertigt mit Präzision und Sorgfalt für Haltbarkeit und Schönheit.",
    benefit_sustainability_title: "Nachhaltigkeit",
    benefit_sustainability_description: "Umweltfreundliche Materialien und energieeffizientes Design für eine bessere Zukunft.",
    benefit_service_title: "Persönlicher Service",
    benefit_service_description: "Engagierte Unterstützung vom Konzept bis zur fertigen Konstruktion.",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Läs ?lang= från URL vid mount (för direktlänkar) - svenska som default
  const [language, setLanguage] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const langParam = urlParams.get('lang');
      if (langParam === 'en' || langParam === 'de') {
        return langParam;
      }
    }
    return 'sv';
  });

  // Uppdatera html lang-attribut vid språkbyte (SEO + tillgänglighet)
  useEffect(() => {
    const langCode = language === 'sv' ? 'sv-SE' : language === 'de' ? 'de-DE' : 'en';
    document.documentElement.lang = langCode;
  }, [language]);

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.sv] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
